import { LogicNameFilter } from "../../../../../utils/constants";
import {
  getTimestampXDaysAgo,
  getTimestampXDaysInFuture,
} from "../../../../../utils/timestamp";
import { filterName, futureTimeValues, pastTimeValues } from "../utils";

const getNHIPoliciesWithSources = (entity) => {
  const nhiPoliciesWithSources = [];
  if (entity?.aws_common_data?.policy_names) {
    nhiPoliciesWithSources.push(
      ...entity.aws_common_data.policy_names
        .filter((policyName) => policyName) // Filter out null/undefined
        .map((policyName) => `${policyName}__aws`)
    );
  }
  return nhiPoliciesWithSources;
};

export const applyNHIFilters = (
  entities,
  appliedFilters,
  filterAndOrLogics
) => {
  let filteredEntities = [...entities];

  // NHI Type
  if (
    filterName.NHI_TYPE in appliedFilters &&
    Object.keys(appliedFilters[filterName.NHI_TYPE]).length > 0
  ) {
    const checkedTypes = Object.keys(
      appliedFilters[filterName.NHI_TYPE]
    ).filter((type) => appliedFilters[filterName.NHI_TYPE][type]);
    if (checkedTypes.length > 0) {
      filteredEntities = filteredEntities.filter((entity) =>
        checkedTypes.includes(entity?.subtype)
      );
    }
  }

  // Connected Email (Related User) - OR logic only (any condition)
  if (
    filterName.NHI_CONNECTED_EMAIL in appliedFilters &&
    Object.keys(appliedFilters[filterName.NHI_CONNECTED_EMAIL]).length > 0
  ) {
    const checkedEmails = new Set(
      Object.keys(appliedFilters[filterName.NHI_CONNECTED_EMAIL]).filter(
        (email) => appliedFilters[filterName.NHI_CONNECTED_EMAIL][email]
      )
    );
    if (checkedEmails.size > 0) {
      filteredEntities = filteredEntities.filter((entity) => {
        const entityEmail = entity?.connected_email;
        if (!entityEmail) return false;
        return checkedEmails.has(entityEmail);
      });
    }
  }

  /// NHI Last used
  if (
    filterName.NHI_LAST_USED in appliedFilters &&
    filterName.NHI_LAST_USED in filterAndOrLogics &&
    Object.keys(appliedFilters[filterName.NHI_LAST_USED]).length > 0
  ) {
    const checkedLastUsed = Object.keys(
      appliedFilters[filterName.NHI_LAST_USED]
    ).filter((key) => appliedFilters[filterName.NHI_LAST_USED][key]);
    if (checkedLastUsed.length > 0) {
      filteredEntities = filteredEntities.filter((entity) => {
        let lastUsedTimestamp;
        if (entity?.okta_token_data?.last_updated_datetime)
          lastUsedTimestamp =
            entity.okta_token_data?.last_updated_datetime?.timestamp_utc;
        if (entity?.aws_secret_data?.last_changed_datetime)
          lastUsedTimestamp =
            entity.aws_secret_data?.last_changed_datetime?.timestamp_utc;
        if (entity?.atlassian_api_key_data?.last_active_at)
          lastUsedTimestamp =
            entity.atlassian_api_key_data?.last_active_at?.timestamp_utc;
        if (entity?.atlassian_api_token_data?.last_active_at)
          lastUsedTimestamp =
            entity.atlassian_api_token_data?.last_active_at?.timestamp_utc;
        if (entity?.github_pat_data?.last_used_at)
          lastUsedTimestamp =
            entity.github_pat_data?.last_used_at?.timestamp_utc;
        if (entity?.github_secret_data?.last_updated_date)
          lastUsedTimestamp =
            entity.github_secret_data?.last_updated_date?.timestamp_utc;
        if (entity?.gitlab_access_token_data?.last_used_at)
          lastUsedTimestamp =
            entity.gitlab_access_token_data?.last_used_at?.timestamp_utc;
        if (!lastUsedTimestamp || lastUsedTimestamp === 0) return false;

        let selectedTimestamp = 0;
        for (const [name, info] of Object.entries(pastTimeValues)) {
          if (appliedFilters[filterName.NHI_LAST_USED][name])
            selectedTimestamp = getTimestampXDaysAgo(info.days);
        }
        if (
          filterAndOrLogics[filterName.NHI_LAST_USED] ===
          LogicNameFilter.TIME_AFTER
        )
          return lastUsedTimestamp > selectedTimestamp;
        else if (
          filterAndOrLogics[filterName.NHI_LAST_USED] ===
          LogicNameFilter.TIME_BEFORE
        )
          return lastUsedTimestamp < selectedTimestamp;
        else return false;
      });
    }
  }

  // Expiration date
  if (
    filterName.EXPIRATION_DATE in appliedFilters &&
    Object.keys(appliedFilters[filterName.EXPIRATION_DATE]).length > 0
  ) {
    const checkedExpirationDate = Object.keys(
      appliedFilters[filterName.EXPIRATION_DATE]
    ).filter((key) => appliedFilters[filterName.EXPIRATION_DATE][key]);
    if (checkedExpirationDate.length > 0) {
      filteredEntities = filteredEntities.filter((entity) => {
        let expirationTimestamp =
          entity?.okta_token_data?.expiration_datetime?.timestamp_utc;
        if (entity?.atlassian_api_key_data?.expires_at)
          expirationTimestamp =
            entity.atlassian_api_key_data?.expires_at?.timestamp_utc;
        if (entity?.atlassian_api_token_data?.expires_at)
          expirationTimestamp =
            entity.atlassian_api_token_data?.expires_at?.timestamp_utc;
        if (entity?.github_pat_data?.expired_date)
          expirationTimestamp =
            entity.github_pat_data?.expired_date?.timestamp_utc;
        if (entity?.gitlab_access_token_data?.expires_at)
          expirationTimestamp =
            entity.gitlab_access_token_data?.expires_at?.timestamp_utc;
        if (!expirationTimestamp || expirationTimestamp === 0) return false;

        let selectedTimestamp = Infinity;
        for (const [name, info] of Object.entries(futureTimeValues)) {
          if (appliedFilters[filterName.EXPIRATION_DATE][name])
            selectedTimestamp = getTimestampXDaysInFuture(info.days);
        }
        return expirationTimestamp < selectedTimestamp;
      });
    }
  }

  // Policies
  if (
    filterName.NHI_POLICIES in appliedFilters &&
    Object.keys(appliedFilters[filterName.NHI_POLICIES]).length > 0
  ) {
    const checkedPolicyKeys = Object.keys(
      appliedFilters[filterName.NHI_POLICIES]
    ).filter((key) => appliedFilters[filterName.NHI_POLICIES][key] === true);
    if (checkedPolicyKeys.length > 0) {
      if (
        filterAndOrLogics[filterName.NHI_POLICIES] === LogicNameFilter.LOGIC_OR
      ) {
        filteredEntities = filteredEntities.filter((entity) => {
          const nhiPoliciesWithSources = getNHIPoliciesWithSources(entity);
          return checkedPolicyKeys.some((policyKey) =>
            nhiPoliciesWithSources.includes(policyKey)
          );
        });
      } else if (
        filterAndOrLogics[filterName.NHI_POLICIES] === LogicNameFilter.LOGIC_AND
      ) {
        filteredEntities = filteredEntities.filter((entity) => {
          const nhiPoliciesWithSources = getNHIPoliciesWithSources(entity);
          return checkedPolicyKeys.every((policyKey) =>
            nhiPoliciesWithSources.includes(policyKey)
          );
        });
      }
    }
  }

  // Rotation enabled
  if (
    filterName.NHI_ROTATION_ENABLED in appliedFilters &&
    Object.keys(appliedFilters[filterName.NHI_ROTATION_ENABLED]).length > 0
  ) {
    const checkedRotationEnabled = Object.keys(
      appliedFilters[filterName.NHI_ROTATION_ENABLED]
    ).filter((key) => appliedFilters[filterName.NHI_ROTATION_ENABLED][key]);
    if (checkedRotationEnabled.length > 0) {
      filteredEntities = filteredEntities.filter((entity) => {
        const keyRotationEnabled = entity?.aws_kms_data?.key_rotation_enabled;
        if (keyRotationEnabled === undefined || keyRotationEnabled === null) {
          return false;
        }
        return checkedRotationEnabled.includes(keyRotationEnabled.toString());
      });
    }
  }

  // Privileged in AD
  if (
    filterName.NHI_PRIVILEGED_IN_AD in appliedFilters &&
    Object.keys(appliedFilters[filterName.NHI_PRIVILEGED_IN_AD]).length > 0
  ) {
    const checkedPrivilegedInAD = Object.keys(
      appliedFilters[filterName.NHI_PRIVILEGED_IN_AD]
    ).filter((key) => appliedFilters[filterName.NHI_PRIVILEGED_IN_AD][key]);
    if (checkedPrivilegedInAD.length > 0) {
      filteredEntities = filteredEntities.filter((entity) => {
        const isPrivilegedInAD =
          entity?.microsoft_active_directory_service_user_data?.is_privileged ||
          false;
        const valueToMatch = isPrivilegedInAD ? "yes" : "no";
        return checkedPrivilegedInAD.includes(valueToMatch);
      });
    }
  }

  entities.length = 0;
  for (let i = 0; i < filteredEntities.length; i++) {
    entities[i] = filteredEntities[i];
  }
};
